/**
 * Helpers for safe writes to Firebase Realtime Database.
 *
 * Unlike Firestore, RTDB rejects any `undefined` value in a write payload with
 * "set failed: value argument contains undefined in property '...'". This util
 * recursively strips undefined values from objects and arrays before writing,
 * while preserving Firebase sentinel objects (e.g. { ".sv": "timestamp" }).
 */

const isFirebaseSentinel = (obj: Record<string, unknown>): boolean => {
  // Server timestamp / increment / etc. — leave untouched
  return ".sv" in obj || ".value" in obj;
};

export const pruneUndefined = <T>(value: T): T => {
  if (value === undefined) return undefined as unknown as T;
  if (value === null) return value;

  if (Array.isArray(value)) {
    return value
      .map((v) => pruneUndefined(v))
      .filter((v) => v !== undefined) as unknown as T;
  }

  if (typeof value === "object") {
    const obj = value as Record<string, unknown>;
    if (isFirebaseSentinel(obj)) return value;
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(obj)) {
      const pruned = pruneUndefined(v);
      if (pruned !== undefined) out[k] = pruned;
    }
    return out as unknown as T;
  }

  return value;
};
